import {
  addDays,
  endOfMonth,
  format,
  formatDistanceToNowStrict,
  isValid,
  parseISO,
  startOfMonth,
  subDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { centsToBRL, formatPercent } from '@marketing-os/shared';

export { centsToBRL, formatPercent };

const numberFormatter = new Intl.NumberFormat('pt-BR');
const compactFormatter = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });
const decimalFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : typeof value === 'number' ? new Date(value) : parseISO(value);
  return isValid(date) ? date : null;
}

export function formatDate(value: string | Date | null | undefined, pattern = 'dd/MM/yyyy'): string {
  const date = toDate(value);
  return date ? format(date, pattern, { locale: ptBR }) : '—';
}

export function formatDateTime(value: string | Date | null | undefined): string {
  return formatDate(value, "dd/MM/yyyy 'às' HH:mm");
}

export function formatTime(value: string | Date | null | undefined): string {
  return formatDate(value, 'HH:mm');
}

export function formatMonthLabel(value: string | Date): string {
  return formatDate(value, "MMMM 'de' yyyy");
}

export function formatWeekday(value: string | Date): string {
  return formatDate(value, 'EEEEEE');
}

export function formatRelative(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return formatDistanceToNowStrict(date, { locale: ptBR, addSuffix: true });
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return numberFormatter.format(Math.round(value));
}

export function formatDecimal(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return decimalFormatter.format(value);
}

export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return compactFormatter.format(value);
}

export function formatMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return '—';
  return centsToBRL(cents);
}

export function formatPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return formatPercent(value, digits);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** `YYYY-MM-DD` — formato aceito pelos filtros da API. */
export function toIsoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export interface Period {
  from: string;
  to: string;
}

export function lastNDays(days: number): Period {
  const today = new Date();
  return { from: toIsoDate(subDays(today, days - 1)), to: toIsoDate(today) };
}

export function currentMonth(): Period {
  const today = new Date();
  return { from: toIsoDate(startOfMonth(today)), to: toIsoDate(endOfMonth(today)) };
}

export function shiftPeriod(period: Period, days: number): Period {
  return {
    from: toIsoDate(addDays(parseISO(period.from), days)),
    to: toIsoDate(addDays(parseISO(period.to), days)),
  };
}

export function periodLabel(period: Period): string {
  return `${formatDate(period.from)} — ${formatDate(period.to)}`;
}

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
